import React, { useState, useEffect, useRef, useCallback } from 'react';
import QuizFlashcards from '../common/QuizFlashcards';
import KaTeXContent from '../common/KaTeXContent';

const THEME_STYLES = {
  blue:   { bg: 'from-blue-600 to-blue-800',   text: 'text-blue-100',  bullet: 'bg-blue-400',  title: 'text-white' },
  green:  { bg: 'from-green-600 to-green-800',  text: 'text-green-100', bullet: 'bg-green-400', title: 'text-white' },
  purple: { bg: 'from-purple-600 to-purple-800',text: 'text-purple-100',bullet: 'bg-purple-400',title: 'text-white' },
  orange: { bg: 'from-orange-500 to-orange-700',text: 'text-orange-100',bullet: 'bg-orange-400',title: 'text-white' },
  red:    { bg: 'from-red-600 to-red-800',      text: 'text-red-100',   bullet: 'bg-red-400',   title: 'text-white' },
  teal:   { bg: 'from-teal-600 to-teal-800',    text: 'text-teal-100',  bullet: 'bg-teal-400',  title: 'text-white' },
  indigo: { bg: 'from-indigo-600 to-indigo-800',text: 'text-indigo-100',bullet: 'bg-indigo-400',title: 'text-white' },
  gray:   { bg: 'from-gray-600 to-gray-800',    text: 'text-gray-100',  bullet: 'bg-gray-400',  title: 'text-white' },
  sky:    { bg: 'from-sky-500 to-sky-700',      text: 'text-sky-100',   bullet: 'bg-sky-400',   title: 'text-white' },
};

const AUTO_ADVANCE_MS = 7000;
const BULLET_INITIAL_DELAY_MS = 1000;
const BULLET_REVEAL_STEP_MS = 2000;

// Spread bullet reveals across a slide's real duration (narration length, or
// the auto-advance fallback) instead of a fixed 800ms/bullet cadence, so a
// slide with many bullets and short narration doesn't finish revealing after
// the audio ends, and a slide with few bullets doesn't sit fully-revealed
// for most of a long narration.
function computeBulletRevealDelay(bulletCount, totalMs) {
  if (bulletCount <= 0 || !totalMs) return 0;
  const revealBudget = Math.min(BULLET_INITIAL_DELAY_MS + BULLET_REVEAL_STEP_MS * bulletCount, totalMs * 0.7);
  return Math.max(revealBudget / (bulletCount + 1), 150);
}

function DiagramPanel({ svgCode, fullWidth = false }) {
  if (!svgCode) return null;

  return (
    <div
      className={`
        ${fullWidth ? 'w-full max-w-lg mx-auto' : 'w-full sm:w-1/2'}
        flex-shrink-0 self-start
      `}
    >
      <div
        className="
          h-64
          rounded-xl
          border border-gray-200
          bg-white
          overflow-hidden
          flex items-center justify-center
          p-2
          [&_svg]:max-w-full
          [&_svg]:max-h-full
          [&_svg]:w-auto
          [&_svg]:h-auto
        "
        dangerouslySetInnerHTML={{ __html: svgCode }}
      />
    </div>
  );
}

function SlideContent({ slide, theme, visibleBullets, showSubtitles, isFullscreen }) {
  const hasDiagram = Boolean(slide.diagram_svg);
  const hasBullets = (slide.bullet_points || []).length > 0;

  return (
    <div className={`bg-gradient-to-br ${theme.bg} p-6 sm:p-10 flex flex-col justify-between ${isFullscreen ? 'flex-1' : 'min-h-[300px]'}`}>
      {/* Slide number + title */}
      <div className="mb-4">
        <h2 className={`text-2xl sm:text-3xl font-bold ${theme.title} leading-tight`}>
          <KaTeXContent content={slide.title} className="inline" />
        </h2>
        {slide.body && (
          <p className={`text-sm sm:text-base ${theme.text} opacity-80 mt-2`}>
            <KaTeXContent content={slide.body} className="inline" />
          </p>
        )}
      </div>

      {/* Main content: bullets + optional diagram side-by-side */}
      <div className={`flex flex-col ${hasDiagram && hasBullets ? 'sm:flex-row' : ''} gap-4 flex-1`}>
        {/* Bullet points */}
        {hasBullets && (
          <ul className={`space-y-3 ${hasDiagram ? 'sm:w-1/2' : 'w-full'}`}>
            {(slide.bullet_points || []).map((bp, i) => (
              <li
                key={i}
                className={`flex items-start gap-3 transition-all duration-500 ${
                  i < visibleBullets ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
              >
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${theme.bullet}`} />
                <span className={`text-base sm:text-lg ${theme.text}`}>
                  <KaTeXContent content={bp} className="inline" />
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Diagram panel */}
        {hasDiagram && <DiagramPanel svgCode={slide.diagram_svg} fullWidth={!hasBullets} />}
      </div>

      {/* Narration subtitle */}
      {showSubtitles && slide.narration && (
        <p className={`text-sm sm:text-base ${theme.text} opacity-75 italic border-t border-white/20 pt-3 mt-3`}>
          <KaTeXContent content={slide.narration} className="inline" />
        </p>
      )}
    </div>
  );
}

export default function VideoPlayer({ video, onClose, inline = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visibleBullets, setVisibleBullets] = useState(0);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const bulletTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const slideStartTimeRef = useRef(0);
  const playerRef = useRef(null);

  const slides = video?.slides || [];
  const slide = slides[currentIndex];
  const theme = THEME_STYLES[slide?.theme] || THEME_STYLES.blue;
  // `quiz` is the canonical field on the video doc; `lesson_kit_quiz`
  // is the legacy key from the first lesson-kit rollout, kept here so
  // older mirrored decks keep their flashcards.
  const quiz = video?.quiz || video?.lesson_kit_quiz;
  const hasQuiz = Boolean(quiz?.questions?.length);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    clearTimeout(timerRef.current);
    clearTimeout(bulletTimerRef.current);
    clearInterval(progressIntervalRef.current);
  }, []);

  const goToSlide = useCallback((index) => {
    if (index < 0 || index >= slides.length || transitioning) return;
    stopAudio();
    setSlideProgress(0);
    setTransitioning(true);
    setVisibleBullets(0);
    setTimeout(() => {
      setCurrentIndex(index);
      setTransitioning(false);
    }, 50);
  }, [slides.length, transitioning, stopAudio]);

  const advance = useCallback(() => {
    if (currentIndex < slides.length - 1) {
      goToSlide(currentIndex + 1);
    } else {
      setIsPlaying(false);
      if (hasQuiz) {
        setShowQuiz(true);
      }
    }
  }, [currentIndex, slides.length, goToSlide, hasQuiz]);

  useEffect(() => {
    if (!isPlaying || !slide) return;
    setVisibleBullets(0);
    setSlideProgress(0);
    slideStartTimeRef.current = Date.now();

    const bulletCount = slide.bullet_points?.length || 0;
    let bulletsStarted = false;
    const startBulletReveal = (totalMs) => {
      if (bulletsStarted || bulletCount === 0) return;
      bulletsStarted = true;
      const perBullet = computeBulletRevealDelay(bulletCount, totalMs);
      let b = 0;
      const showNextBullet = () => {
        if (b < bulletCount) {
          b++;
          setVisibleBullets(b);
          bulletTimerRef.current = setTimeout(showNextBullet, perBullet);
        }
      };
      bulletTimerRef.current = setTimeout(showNextBullet, perBullet);
    };

    if (slide.audio_b64) {
      const dataUrl = `data:audio/mpeg;base64,${slide.audio_b64}`;
      if (audioRef.current) {
        audioRef.current.src = dataUrl;
        audioRef.current.currentTime = 0;
        audioRef.current.onloadedmetadata = () => {
          const durationMs = (audioRef.current?.duration || 0) * 1000;
          startBulletReveal(durationMs || AUTO_ADVANCE_MS);
        };
        audioRef.current.onended = () => {
          setSlideProgress(1);
          timerRef.current = setTimeout(advance, 800);
        };
        audioRef.current.play().catch(() => {});
        // Metadata should load near-instantly for an inline data URL, but
        // guard against it never firing so bullets still reveal.
        bulletTimerRef.current = setTimeout(() => startBulletReveal(AUTO_ADVANCE_MS), 1000);

        progressIntervalRef.current = setInterval(() => {
          const duration = audioRef.current?.duration || 0;
          const currentTime = audioRef.current?.currentTime || 0;
          if (duration > 0) {
            setSlideProgress(Math.min(currentTime / duration, 1));
          }
        }, 100);
      }
    } else {
      startBulletReveal(AUTO_ADVANCE_MS);
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - slideStartTimeRef.current;
        setSlideProgress(Math.min(elapsed / AUTO_ADVANCE_MS, 1));
      }, 100);
      timerRef.current = setTimeout(advance, AUTO_ADVANCE_MS);
    }

    return () => { stopAudio(); };
  }, [isPlaying, currentIndex, slide, advance, stopAudio]);

  useEffect(() => {
    if (!isPlaying && slide) {
      setVisibleBullets(slide.bullet_points?.length || 0);
      setSlideProgress(0);
    }
  }, [isPlaying, currentIndex, slide]);

  const isMobileDevice = () =>
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  const togglePlay = () => {
    if (isPlaying) {
      stopAudio();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      // Never auto-fullscreen on mobile — let users control fullscreen manually
      if (!isFullscreen && !isMobileDevice() && playerRef.current?.requestFullscreen) {
        playerRef.current.requestFullscreen().catch(() => {});
      }
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else if (playerRef.current?.requestFullscreen) {
      playerRef.current.requestFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  if (!slide) return null;

  const controls = (
    <div className="bg-gray-900 px-3 py-4 sm:px-6 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
      <button onClick={() => goToSlide(currentIndex - 1)} disabled={currentIndex === 0} className="text-white/60 hover:text-white disabled:opacity-30 transition-colors text-xl sm:text-sm px-2">&#8592;</button>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <button onClick={togglePlay} className="flex items-center gap-2 bg-white text-gray-900 font-semibold px-2 py-2 sm:px-4 sm:py-2 rounded-full hover:bg-gray-100 transition-colors text-xs sm:text-sm">
          <span>{isPlaying ? '⏸' : '▶'}</span>
          <span className="hidden sm:inline">{isPlaying ? 'Pause' : 'Play'}</span>
        </button>
        <button
          onClick={() => setShowSubtitles((p) => !p)}
          className={`border px-2 py-2 sm:px-3 rounded-full text-xs transition-colors ${
            showSubtitles
              ? 'bg-white text-black border-white hover:bg-gray-100'
              : 'bg-black text-white border-white/25 hover:border-white/60 hover:bg-gray-900'
          }`}
        >
          CC
        </button>
        <button onClick={toggleFullscreen} className="text-white/80 hover:text-white border border-white/25 hover:border-white/60 px-2 py-2 sm:px-3 rounded-full text-xs transition-colors">
          {isFullscreen ? '⌞⌝' : '⛶'}
        </button>
        {hasQuiz && !showQuiz && (
          <button
            onClick={() => { stopAudio(); setIsPlaying(false); setShowQuiz(true); }}
            className="bg-amber-600 text-gray-900 font-semibold px-3 py-2 rounded-full text-xs sm:text-sm hover:bg-amber-300"
          >
            <span className="sm:hidden" aria-hidden="true">🎴 Quiz</span>
            <span className="hidden sm:inline">🎴 Start Quiz</span>
          </button>
        )}
      </div>
      <button onClick={() => goToSlide(currentIndex + 1)} disabled={currentIndex === slides.length - 1} className="text-white/60 hover:text-white disabled:opacity-30 transition-colors text-xl px-2">&#8594;</button>
    </div>
  );

  const dots = (
    <div className="bg-gray-900 pb-4 flex justify-center gap-1.5">
      {slides.map((_, i) => (
        <button key={i} onClick={() => goToSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-white w-4' : 'bg-white/30'}`} />
      ))}
    </div>
  );

  const progressBar = (
    <div className="w-full bg-black/20 h-1">
      <div className="h-1 bg-white/60 transition-all duration-100" style={{ width: `${((currentIndex + slideProgress) / slides.length) * 100}%` }} />
    </div>
  );

  if (inline) {
    return (
      <div ref={playerRef} className="w-full rounded-2xl shadow-2xl overflow-hidden">
        <div className={`transition-opacity duration-300 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
          <div className="px-6 pt-4 bg-gradient-to-br from-gray-800 to-gray-900 flex justify-between">
            <span className="text-sm text-white/60">
              {showQuiz ? 'Quiz time' : `${currentIndex + 1} / ${slides.length}`}
            </span>
          </div>
          {showQuiz ? (
            <QuizFlashcards
              quiz={quiz}
              onClose={() => setShowQuiz(false)}
              onReplayDeck={() => { setShowQuiz(false); goToSlide(0); }}
            />
          ) : (
            <SlideContent slide={slide} theme={theme} visibleBullets={visibleBullets} showSubtitles={showSubtitles} isFullscreen={false} />
          )}
        </div>
        {!showQuiz && progressBar}
        {!showQuiz && controls}
        {!showQuiz && dots}
        <audio ref={audioRef} className="hidden" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div
        ref={playerRef}
        className={`relative w-full shadow-2xl overflow-hidden flex flex-col ${isFullscreen ? 'h-full rounded-none' : 'max-w-3xl mx-4 rounded-2xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slide number + close */}
        <div className={`bg-gradient-to-br ${theme.bg} px-8 pt-5 pb-2 flex justify-between items-center transition-opacity duration-300 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
          <span className={`text-sm font-medium ${theme.text} opacity-70`}>
            {showQuiz ? 'Quiz time' : `${currentIndex + 1} / ${slides.length}`}
          </span>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all" aria-label="Close"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>

        <div className={`flex flex-col flex-1 transition-opacity duration-300 ${transitioning ? 'opacity-0' : 'opacity-100'}`}>
          {showQuiz ? (
            <QuizFlashcards
              quiz={quiz}
              onClose={() => setShowQuiz(false)}
              onReplayDeck={() => { setShowQuiz(false); goToSlide(0); }}
            />
          ) : (
            <SlideContent slide={slide} theme={theme} visibleBullets={visibleBullets} showSubtitles={showSubtitles} isFullscreen={isFullscreen} />
          )}
        </div>

        {!showQuiz && progressBar}
        {!showQuiz && controls}
        {!showQuiz && dots}
      </div>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
}
  